/**
 * Lesson library search: each query word must prefix-match a whole word in the
 * haystack (e.g. "prog" → "program", "hello comp" → words starting with those
 * prefixes). Does not match scattered letters (subsequence) or arbitrary
 * substrings inside unrelated words.
 */
export function fuzzyScore(query: string, haystack: string): number | null {
  const q = query.trim().toLowerCase();
  const h = haystack.toLowerCase();
  if (!q) return 0;
  if (!h) return null;

  const qWords = q.split(/\s+/).filter(Boolean);
  if (qWords.length === 0) return 0;

  const hWords = h.split(/[^a-z0-9]+/).filter(Boolean);
  if (hWords.length === 0) return null;

  if (!qWords.every((qw) => hWords.some((hw) => hw.startsWith(qw)))) {
    return null;
  }

  let score = 500;
  for (const qw of qWords) {
    const wordIdx = hWords.findIndex((hw) => hw.startsWith(qw));
    const hw = hWords[wordIdx]!;
    score -= wordIdx * 3;
    if (hw === qw) {
      score += 220;
    } else {
      score += 120 - (hw.length - qw.length);
    }
  }
  return score - h.length * 0.01;
}

export function fuzzyFilter<T>(
  items: readonly T[],
  query: string,
  keys: (item: T) => (string | null | undefined)[]
): T[] {
  const q = query.trim();
  if (!q) return [...items];
  return items
    .map((item) => {
      let best: number | null = null;
      for (const key of keys(item)) {
        if (!key) continue;
        const s = fuzzyScore(q, key);
        if (s !== null && (best === null || s > best)) best = s;
      }
      return { item, score: best };
    })
    .filter((r): r is { item: T; score: number } => r.score !== null)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.item);
}
